import { useDebouncedCallback } from '@mantine/hooks'
import { SITE_ENV_LABELS, SITE_ENVS, SITE_PRODUCTS, SITE_STATUS_LABELS, SITE_STATUSES, SiteEnv, SiteProduct, SiteWithStacks, SiteStatus, mediaStatusOf } from '@pubstack/common/src/site'
import { parseAsInteger, parseAsNumberLiteral, parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs'
import { FunctionComponent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSitesWithPagination } from '~/api/app/sites.api'
import { api } from '~/api/HttpProvider'
import { Colors } from '~/assets/style/colors'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { AbsentValue, ActionButtons, ActionsTableCell, NoResultsTableCell } from '~/components/_styled/table'
import Button from '~/components/Button'
import { CopyableId } from '~/components/CopyableId'
import { Icon } from '~/components/Icon'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { BaseInputElement } from '~/components/Input'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { PageContent } from '~/components/PageContent'
import { Popover, PopoverContentWrapper, PopoverLine } from '~/components/Popover'
import { PAGE_LIMIT_OPTIONS, Pagination } from '~/components/Pagination'
import { UnstyledSelect } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Tooltip } from '~/components/Tooltip'
import Table, { Column, onColumnSort, SortableColumn, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow, { ROW_CELL_CLASSES } from '~/components/table/TableRow'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useNodeStaffAccess } from '~/hooks/useNodeStaffAccess'
import { downloadCSVData } from '~/utils/fileDownload'
import { AnalyticsTagModal } from './AnalyticsTagModal'
import { ArchiveMediaModal } from './ArchiveMediaModal'
import { CreateSiteFlyout } from './CreateSiteFlyout'
import { EditSiteFlyout } from './EditSiteFlyout'
import { SITE_PRODUCT_LABEL, siteProductOf } from './siteProduct'
import { SiteProductIndicator } from './SiteProductIndicator'
import { SiteEnvIcon } from './SiteEnvIcon'
import { SiteStatusIndicator } from './SiteStatusIndicator'
import { PATH_BY_PRODUCT, Product, useOptionalProduct } from '~/hooks/useProduct'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'

/**
 * What the table's columns sort by. `mediaStatus` is not a field of a media: the server ranks it the way
 * `mediaStatusOf` reads it, so the list sorts and filters on the status its own column shows.
 */
type SortableSite = SiteWithStacks & { mediaStatus: SiteStatus }

// Constants
const SEARCH_DEBOUNCE_TIME = 300 // ms
const ITEMS_PER_PAGE = 10

type SiteTrProps = {
  site: SiteWithStacks
  scopeId: string
  canArchive: boolean
}

/**
 * Everything about a row comes from the media, not from the page it is listed on: one list now holds
 * both products, so the media's own product decides its status, its stacks and where clicking it goes.
 *
 * Exported for `SitesPage.spec.tsx`.
 */
export const SiteTr: FunctionComponent<SiteTrProps> = ({ site, scopeId, canArchive }) => {
  // Hooks
  const navigate = useNavigate()
  const flyout = useGlobalFlyout()
  const modal = useGlobalModal()

  const siteProduct = siteProductOf(site)
  // Analytics medias have no product tree to open, so they fall back to One's labels and never navigate.
  const product: Product = siteProduct === 'pbs' ? 'pbs' : 'adm'

  // Tracking labels
  const EDIT_TRACKING_LABEL: TrackingLabel = `${product}-sites-edit`
  const VIEW_TRACKING_LABEL: TrackingLabel = `${product}-sites-view`
  const NAVIGATE_TRACKING_LABEL: TrackingLabel = `${product}-sites-navigate`

  const siteHasStacks = site.stacks.filter((v) => v.status !== 'archived' && v.type === product).length > 0
  const statusField = mediaStatusOf(site)
  // An Analytics media has no stack or activation to view — the only action is giving it a product in the edit flyout.
  const isAnalytics = siteProduct === 'analytics'

  const openEditFlyout = () =>
    flyout.open(
      EditSiteFlyout,
      { color: Colors.Kaiminus, closeTrackingLabel: `${EDIT_TRACKING_LABEL}-close` },
      {
        site,
        scopeId,
        onClose: () => {
          flyout.close()
        },
      }
    )

  const sitePath = `${PATH_BY_PRODUCT[product]}/sites/${site.id}/${siteHasStacks ? 'stacks' : 'activation'}`

  /**
   * The pen opens the media's own pages, where the settings too big for a flyout are edited. Only One and
   * Node have those pages, which is why the button is theirs alone.
   */
  const handleEditClick = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.stopPropagation()
    event.preventDefault()
    navigate(sitePath)
  }

  const handleViewClick = (event?: React.MouseEvent<Element, MouseEvent>) => {
    event?.stopPropagation()
    event?.preventDefault()
    openEditFlyout()
  }

  const handleArchiveClick = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.stopPropagation()
    event.preventDefault()
    modal.open(ArchiveMediaModal, { media: site })
  }

  return (
    <TableRow
      className={ROW_CELL_CLASSES}
      // A One or Node media opens its own pages, where its stacks and its activation are — the same place
      // the pen goes. The flyout stays one click away on the eye, and remains the whole of what an
      // Analytics media can open: it owns no product tree, and `sitePath` would send it to One's.
      clickable={{
        onClick: isAnalytics ? openEditFlyout : () => navigate(sitePath),
        trackingLabel: isAnalytics ? VIEW_TRACKING_LABEL : NAVIGATE_TRACKING_LABEL,
      }}
    >
      <TableCell>
        <span className={'flex items-center gap-2'}>
          <SiteEnvIcon env={site.env ?? 'web'} />
          <span>
            {site.name}
            {/* Under the name, where the ad unit list puts the ad server name. The weight is set here
                because the table bolds every first cell. */}
            <CopyableId id={site.id} label={'Media ID'} trackingLabel={'common-medias-id-copy'} className={'flex text-xs/[20px] font-normal text-slate-grey'} />
          </span>
        </span>
      </TableCell>
      <TableCell>
        <SiteProductIndicator product={siteProduct} />
      </TableCell>

      <TableCell>
        {/* Where the row now goes too, but tracked apart: "Out of sync" or "To deploy" is read here and
            acted on there, and how often that is the reason for leaving the list is worth its own count.
            An Analytics media has no stacks and no pages, so its status stays plain text. */}
        {isAnalytics ? (
          <SiteStatusIndicator status={statusField ?? 'none'} />
        ) : (
          <button
            type={'button'}
            className={'flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-left'}
            onClick={handleEditClick}
            data-tracking={`${product}-sites-status-navigate`}
          >
            <SiteStatusIndicator status={statusField ?? 'none'} />
            <Icon name={'open_in_new'} width={'14px'} fill={Colors.SlateGrey} />
          </button>
        )}
      </TableCell>

      <TableCell>
        {site.pbjsVariableName ? <code className={'inline-block max-w-full truncate align-bottom font-inconsolata text-sm/[22px]'}>{site.pbjsVariableName}</code> : <AbsentValue>—</AbsentValue>}
      </TableCell>

      <ActionsTableCell>
        <ActionButtons>
          <Tooltip title="View">
            <Button iconName="view" variant="tertiary" title="View" onClick={handleViewClick} trackingLabel={VIEW_TRACKING_LABEL} />
          </Tooltip>
          {/* Only where there is somewhere to go: an Analytics media's pen would land back on this flyout. */}
          {!isAnalytics && (
            <Tooltip title="Edit">
              <Button iconName="edit" variant="tertiary" title="Edit" onClick={handleEditClick} trackingLabel={EDIT_TRACKING_LABEL} />
            </Tooltip>
          )}
          {canArchive && (
            <Tooltip title="Archive">
              <Button iconName="archive" variant="tertiary" title="Archive" onClick={handleArchiveClick} trackingLabel={'common-medias-archive'} />
            </Tooltip>
          )}
        </ActionButtons>
      </ActionsTableCell>
    </TableRow>
  )
}

type DebouncedSearchInput = {
  value: string
  onChange: (value: string) => void
}

const DebouncedSearchInput: FunctionComponent<DebouncedSearchInput> = ({ value, onChange }) => {
  const [localValue, setLocalValue] = useState(value)
  // Resync from the `value` prop ("previous value" pattern — adjust during render).
  const [prevValue, setPrevValue] = useState(value)
  if (value !== prevValue) {
    setPrevValue(value)
    setLocalValue(value)
  }

  const debouncedOnChange = useDebouncedCallback(onChange, SEARCH_DEBOUNCE_TIME)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value)
    debouncedOnChange(e.target.value)
  }

  return <BaseInputElement type="text" iconLeft="search" labelIsPlaceholder label="Search" value={localValue} onChange={handleChange} />
}

export const SitesPage: FunctionComponent = () => {
  // Context
  // `null` on the unified list under /monetization/inventory, which shows every media whatever it runs
  // on. The two product mounts are gone; this stays optional only until nothing renders it per-product.
  const product = useOptionalProduct()
  // On a product list too: asking for that product's own field would sort and filter on a status the
  // column does not show, and miss exactly the rows this exists to surface.
  const statusKey = 'mediaStatus'
  // Tracking labels must resolve to a real prefix, and the unified list belongs to no product.
  const labelProduct = product ?? 'common'

  // Constants
  const STATUS_OPTIONS: SelectOptionProp<SiteStatus | 'all'>[] = [
    {
      label: 'All',
      value: 'all',
      trackingLabel: `${labelProduct}-sites-status-all-apply`,
    },
    {
      label: SITE_STATUS_LABELS['to-configure'],
      value: 'to-configure',
      trackingLabel: `${labelProduct}-sites-status-to-configure-apply`,
    },
    {
      label: SITE_STATUS_LABELS['to-deploy'],
      value: 'to-deploy',
      trackingLabel: `${labelProduct}-sites-status-to-deploy-apply`,
    },
    {
      label: SITE_STATUS_LABELS['out-of-sync'],
      value: 'out-of-sync',
      trackingLabel: `${labelProduct}-sites-status-out-of-sync-apply`,
    },
    {
      label: SITE_STATUS_LABELS['up-to-date'],
      value: 'up-to-date',
      trackingLabel: `${labelProduct}-sites-status-up-to-date-apply`,
    },
    {
      label: SITE_STATUS_LABELS.inactive,
      value: 'inactive',
      trackingLabel: `${labelProduct}-sites-status-inactive-apply`,
    },
  ]

  const ENV_OPTIONS: SelectOptionProp<SiteEnv | 'all'>[] = [
    { label: 'All', value: 'all', trackingLabel: `${labelProduct}-sites-env-all-apply` },
    ...SITE_ENVS.map((env) => ({ label: SITE_ENV_LABELS[env], value: env, trackingLabel: `${labelProduct}-sites-env-${env}-apply` as TrackingLabel })),
  ]

  // Ordered the way the products are sold, not alphabetically, and Analytics last: it is the absence of a
  // product rather than one of them.
  const PRODUCT_OPTIONS: SelectOptionProp<SiteProduct | 'all'>[] = [
    { label: 'All', value: 'all', trackingLabel: `${labelProduct}-sites-plan-all-apply` },
    ...(['adm', 'pbs', 'analytics'] as const).map((product) => ({
      label: SITE_PRODUCT_LABEL[product],
      value: product,
      trackingLabel: `${labelProduct}-sites-plan-${product}-apply` as TrackingLabel,
    })),
  ]

  // Tracking labels
  const CREATE_TRACKING_LABEL: TrackingLabel = `${labelProduct}-sites-new`

  const user = useAuthenticatedUser()
  const { hasNode, isStaffOnNode } = useNodeStaffAccess()
  const [actionsOpen, setActionsOpen] = useState(false)
  // Archiving a media, and seeing the ones already archived, are the same right — the one the API gates as
  // `owner`, and the one `useScopePermissions` was built to answer.
  // Adding a media is a `user`'s to ask for, not to do: it commits the scope to a product, and on One or
  // Node that commitment is one-way. The button stays on screen saying why — seeing that the capability
  // exists is half of knowing to ask an owner for it.
  const { canArchive, canCreate, disabledReason } = useScopePermissions()

  const [columns, setColumns] = useState<TableColumns<SortableSite>>([
    {
      // The sorted one on arrival, matching `sortBy`'s default below: the caret has to be under the column
      // the rows are actually ordered by from the first paint, not a tick later once the two are synced.
      name: 'Media',
      isSortable: true,
      order: 'ascending',
      attributeSort: 'name',
    },
    {
      // Derived from two flags, so there is no stored field for the server-side sort to use.
      name: 'Product',
      isSortable: false,
    },
    {
      name: 'Status',
      isSortable: true,
      order: 'none',
      attributeSort: statusKey,
    },
    {
      name: 'Prebid variable',
      isSortable: false,
    },
    {
      name: 'Actions',
      isSortable: false,
    } satisfies Column,
  ])
  // State
  const [page, setPage] = useQueryState<number>('page', parseAsInteger.withDefault(1).withOptions({ clearOnDefault: false }))
  const [limit, setLimit] = useQueryState<number>('limit', parseAsNumberLiteral(PAGE_LIMIT_OPTIONS).withDefault(ITEMS_PER_PAGE).withOptions({ clearOnDefault: false }))
  const [search, setSearch] = useQueryState<string>('search', parseAsString.withDefault(''))
  const [status, setStatus] = useQueryState<SiteStatus | 'all'>(
    statusKey,
    parseAsStringLiteral([...SITE_STATUSES, 'all'])
      .withDefault('all')
      .withOptions({ history: 'push' })
  )
  // Two more ways to narrow, both URL-synced like the status so a filtered list stays a shareable link.
  // `env` is a stored field; `productColumn` is derived server-side the way the Product column derives it.
  const [env, setEnv] = useQueryState<SiteEnv | 'all'>(
    'env',
    parseAsStringLiteral([...SITE_ENVS, 'all'])
      .withDefault('all')
      .withOptions({ history: 'push' })
  )
  const [productColumn, setProductColumn] = useQueryState<SiteProduct | 'all'>(
    'mediaProduct',
    parseAsStringLiteral([...SITE_PRODUCTS, 'all'])
      .withDefault('all')
      .withOptions({ history: 'push' })
  )
  const [sortBy, setSortBy] = useQueryState<string>(
    'sortBy',
    parseAsStringLiteral((columns.filter((c) => c.isSortable) as SortableColumn[]).map((c) => c.attributeSort) as readonly string[])
      // Every media list opens on the name, product pages included. A list is first of all something to
      // find a media in, and a name is what a reader arrives holding — status is what they came to check
      // about the one they found, and the Status column still sorts on it the moment it is asked.
      .withDefault('name')
      .withOptions({ clearOnDefault: false })
  )
  const [sortOrder, setSortOrder] = useQueryState<'ascending' | 'descending'>(
    'sortOrder',
    parseAsStringLiteral(['ascending', 'descending']).withDefault('ascending').withOptions({ clearOnDefault: false })
  )

  // Hooks
  const navigate = useNavigate()
  const flyout = useGlobalFlyout()
  const modal = useGlobalModal()
  const [isExporting, setIsExporting] = useState(false)
  const breadcrumbs = useBreadcrumbs()
  const scopeId = user.currentScopeId
  const hasAnalyticsFeature = user.hasFeature('analytics')

  // API Hooks
  const {
    data: { data: sites, pagination: sitesPagination },
    isLoading: isLoadingSites,
  } = useSitesWithPagination({
    scopeId,
    filters: {
      search,
      // Narrowed to one product only when the page is: the unified list is the whole inventory.
      ...(product ? { siteProduct: product } : {}),
      ...(status !== 'all' ? { [statusKey]: [status as SiteStatus] } : {}),
      ...(env !== 'all' ? { env: [env] } : {}),
      ...(productColumn !== 'all' ? { mediaProduct: [productColumn] } : {}),
    },
    pagination: { page, limit },
    sortBy,
    sortOrder,
  })

  const handleNewClick = () =>
    flyout.open(
      CreateSiteFlyout,
      { color: Colors.Kaiminus, closeTrackingLabel: `${CREATE_TRACKING_LABEL}-close` },
      {
        scopeId,
        onClose: (sites) => {
          flyout.close()
          // The tag carries the media's own id, so a batch has no single tag to hand out — but showing
          // nothing left a bulk add with no sign that installing one was the next step at all. The
          // snippets go up once with the id as a placeholder instead.
          //
          // Only when the batch shares an environment: the instructions differ per environment, and a
          // mixed batch would be shown one set that is wrong for the rest of it.
          const envs = new Set(sites.map((site) => site.env ?? 'web'))
          hasAnalyticsFeature && sites.length > 0 && envs.size === 1 && modal.open(AnalyticsTagModal, { site: sites[0], mediaCount: sites.length })
        },
      }
    )

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const handleStatusChange = (e: SiteStatus | 'all') => {
    setStatus(e)
    setPage(1)
  }

  const handleEnvChange = (e: SiteEnv | 'all') => {
    setEnv(e)
    setPage(1)
  }

  const handleProductColumnChange = (e: SiteProduct | 'all') => {
    setProductColumn(e)
    setPage(1)
  }

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit)
    setPage(1)
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const params = new URLSearchParams()
      params.set('format', 'csv')
      if (search) params.set('search', search)
      if (status !== 'all') params.set(statusKey, status)
      if (env !== 'all') params.set('env', env)
      if (productColumn !== 'all') params.set('mediaProduct', productColumn)
      // Only on a product list: the unified list is the whole inventory, and the export matches it.
      if (product) params.set('product', product)
      const { data: csv } = await api.get(`/sites/pagination?${params.toString()}`)
      downloadCSVData(csv, `${labelProduct}-sites`)
    } finally {
      setIsExporting(false)
    }
  }

  // On mount, set initial states to sync them with URL query params
  useEffect(() => {
    setPage(page)
    setLimit(limit)
    setStatus(status)
    setSortBy(sortBy)
    setSortOrder(sortOrder)
  }, [])

  // Sync column sort state with URL query params
  useEffect(() => {
    const currentActiveSortColumn = columns.find((c) => ['ascending', 'descending'].includes((c as SortableColumn<SortableSite>).order)) as SortableColumn<SortableSite> | undefined
    if (currentActiveSortColumn?.name !== sortBy || currentActiveSortColumn?.order !== sortOrder) {
      // Syncs column sort state from the URL query params.
      // oxlint-disable-next-line react/set-state-in-effect
      setColumns(
        columns.map((c) => {
          if ((c as SortableColumn<SortableSite>).attributeSort === sortBy) {
            return { ...c, order: sortOrder }
          } else {
            return c.isSortable ? { ...c, order: 'none' } : c
          }
        })
      )
    }
  }, [sortBy, sortOrder])

  return (
    <PageContent breadcrumbs={breadcrumbs}>
      <div className={'flex flex-col gap-4'}>
        {/* Wraps rather than overflowing. Six controls on one non-wrapping line squeezed the search box to
            nothing on a laptop and pushed the last button off the edge; the filters now keep their width and
            the row grows a second line instead. The secondary acts live behind one menu at the end, so what
            wraps is the filtering, which is what the row is for. */}
        <div className={'flex w-full flex-wrap items-center gap-4'}>
          <DebouncedSearchInput value={search} onChange={handleSearchChange} />
          {/* Only on the unified list: on a product page the product is already the page, and picking the
              other one would ask for the medias this list is defined to exclude. */}
          {!product && <UnstyledSelect label="Product" options={PRODUCT_OPTIONS} value={productColumn as string} onChange={(e) => handleProductColumnChange(e.value as SiteProduct | 'all')} />}
          <UnstyledSelect label="Environment" options={ENV_OPTIONS} value={env as string} onChange={(e) => handleEnvChange(e.value as SiteEnv | 'all')} />
          <UnstyledSelect label="Status" options={STATUS_OPTIONS} value={status as string} onChange={(e) => handleStatusChange(e.value as SiteStatus | 'all')} />
          {/* Pushes the overflow menu to the end of whichever line it lands on. */}
          <div className={'flex-1'} />
          {/* Down from the page header onto the table's own row, immediately left of the menu holding the rest.
              Adding a media acts on this list, so it belongs with the other things done to it rather than up
              beside the breadcrumbs — and the ad units list, one menu entry away, already reads this way. */}
          <Tooltip title={disabledReason} disabled={canCreate}>
            <Button iconName="add_site" variant="primary" disabled={!canCreate} onClick={handleNewClick} trackingLabel={CREATE_TRACKING_LABEL}>
              Add media
            </Button>
          </Tooltip>
          {/* The acts that are not filtering, behind one control. Each was a button of its own competing with
              the filters for the same line, and none of them is what the reader came to this list to do. */}
          <Popover
            align={'end'}
            open={actionsOpen}
            setOpen={setActionsOpen}
            trigger={<Button variant={'tertiary'} iconName={'more_vert'} title={'More actions'} trackingLabel={`${labelProduct}-sites-actions-view`} />}
          >
            <PopoverContentWrapper>
              <PopoverLine
                onClick={() => {
                  setActionsOpen(false)
                  handleExport()
                }}
                data-tracking-label={`${labelProduct}-sites-download`}
              >
                <Icon width={'20px'} name={isExporting ? 'pubstack' : 'download'} />
                <span>{isExporting ? 'Downloading...' : 'Download media list'}</span>
              </PopoverLine>
              {/* Node deploys from its own page, and the medias list is where a reader arrives asking which
                  ones are live — so the way there belongs on this row. Owners only, matching the page itself. */}
              {hasNode && canArchive && (
                <PopoverLine
                  onClick={() => {
                    setActionsOpen(false)
                    navigate('/monetization/inventory/deployment-center')
                  }}
                  data-tracking-label={'common-medias-deployment-center-navigate'}
                >
                  <Icon width={'20px'} name={'send'} />
                  <span>Deployment center</span>
                  {isStaffOnNode && <Icon width={'14px'} name={'admin'} fill={Colors.SlateGrey} />}
                </PopoverLine>
              )}
              {/* The only way to the archived medias. Owners only, because the page itself is owner-gated —
                  a link everyone can see leading to a 403 is worse than no link. */}
              {canArchive && (
                <PopoverLine
                  onClick={() => {
                    setActionsOpen(false)
                    navigate('/monetization/inventory/medias/archived')
                  }}
                  data-tracking-label={'common-medias-archived-navigate'}
                >
                  <Icon width={'20px'} name={'archive'} />
                  <span>Archived medias</span>
                </PopoverLine>
              )}
            </PopoverContentWrapper>
          </Popover>
        </div>

        {isLoadingSites || sites.length > 0 ? (
          <>
            <Table
              columns={columns}
              isLoading={isLoadingSites}
              onClickHeading={(column) => {
                onColumnSort(columns, column, setColumns)
                const newSortOrder = (columns.find((c) => c === column) as SortableColumn<SortableSite>)?.order
                setSortOrder(newSortOrder === 'ascending' ? 'descending' : 'ascending')
                setSortBy((column as SortableColumn<SortableSite>).attributeSort)
              }}
            >
              {sites.length > 0 ? (
                sites.map((site) => <SiteTr key={site.id} site={site} scopeId={scopeId} canArchive={canArchive} />)
              ) : (
                <TableRow>
                  <NoResultsTableCell colspan={columns.length}>No results</NoResultsTableCell>
                </TableRow>
              )}
            </Table>

            <Pagination
              currentPage={page}
              totalPages={sitesPagination.totalPages}
              limit={limit}
              totalItems={sitesPagination.total}
              onPageChange={(e) => setPage(e)}
              onLimitChange={handleLimitChange}
            />
          </>
        ) : (
          <IllustrationMessage iconName="night_sky" iconSize="190px" title="" message="No media found." />
        )}
      </div>
    </PageContent>
  )
}
